import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react";

import { cn } from "@btnet/utilities-core";

import {
    getDisambiguatingSourceLabel,
    isKnownTemplateVariable,
    type ITemplateVariableAttributes,
    type ITemplateVariableOptions,
    resolveTemplateVariableText,
    resolveTemplateVariableValue,
} from "commonComponents/btWrappers/editor/core/extensions/custom/templateVariable/TemplateVariableExtension";

export function TemplateVariableNodeView({ node, selected, editor, extension }: NodeViewProps) {
    const attrs = node.attrs as ITemplateVariableAttributes;
    const { sources, readOnlyMode } = extension.options as ITemplateVariableOptions;
    // Flag a chip whose key is no longer in its source (e.g. a removed variable) so it doesn't look valid.
    const isKnown = isKnownTemplateVariable(attrs, sources);
    const name = attrs.templateFriendlyName || attrs.templateKey;
    // The configured value for this key, if the source supplies one; shown beside the name while editing.
    const resolvedValue = resolveTemplateVariableValue(attrs, sources);
    // Only set when the same key exists in more than one source, to tell the chips apart.
    const sourcePrefix = getDisambiguatingSourceLabel(attrs, sources);

    // Read-only in "replaceVariables" mode: render the resolved value as plain text so the document
    // reads as finished output. "renderChips" falls through to the styled chip render below (the same
    // as edit mode) so the reader can still see which parts are templated.
    if (!editor.isEditable && readOnlyMode === "replaceVariables") {
        return (
            <NodeViewWrapper as="span">
                {resolveTemplateVariableText(attrs, sources)}
            </NodeViewWrapper>
        );
    }

    return (
        <NodeViewWrapper as="span" className="whitespace-nowrap">
            <span
                data-testid={`templateVariable-${attrs.templateNamespace}-${attrs.templateKey}`}
                className={cn(
                    "inline-flex items-center gap-xss rounded-sm px-xss font-medium",
                    // Selected: solid fill with inverted text for clear contrast.
                    selected
                        ? isKnown
                            ? "bg-blue-60 text-fg-on-color"
                            : "bg-fg-error text-fg-on-color"
                        : isKnown
                          ? "bg-surface-selected text-fg-primary"
                          : "bg-surface-error-subtle text-fg-error",
                )}
            >
                {sourcePrefix !== undefined && `${sourcePrefix}: `}
                {name}
                {resolvedValue !== undefined && (
                    <span
                        className={cn(
                            "font-normal",
                            selected ? "text-fg-on-color" : "text-fg-secondary",
                        )}
                    >
                        {resolvedValue}
                    </span>
                )}
            </span>
        </NodeViewWrapper>
    );
}
